import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/**
 * Luca e-Fatura/e-İrsaliye salt okuma (ADR 2026-10-31) — panel veri katmanı.
 *
 * KİMLİK (TCKN + parola) bu dosyada react-query'ye GİRMEZ: `saveLucaConnection` bilerek bir
 * `useMutation` değil, düz bir işlev — `useMutation` değişkenleri mutation önbelleğinde tutar
 * (KVKK K4). Sunucu kimliği hiçbir yanıtta döndürmez; panel yalnız `hasCredentials` görür.
 *
 * PARA: tutarlar sunucudan string gelir; panel toplama/karşılaştırma yapmaz (kural 4).
 */

export type LucaConnStatus = 'PENDING' | 'ACTIVE' | 'ERROR' | 'DISABLED';
export type LucaDocumentKind = 'DESPATCH_IN' | 'EARCHIVE_OUT';

export interface LucaConnection {
  status: LucaConnStatus;
  hasCredentials: boolean;
  companyId: number | null;
  consentAt: string | null;
  lastFetchAt: string | null;
  lastSuccessAt: string | null;
  /** Yalnız sabit hata kodu (`LUCA_*`). */
  lastError: string | null;
  errorCount: number;
  enabled: boolean;
  canActivate: boolean;
}

export interface LucaDocumentRow {
  id: string;
  kind: LucaDocumentKind;
  ettn: string;
  documentNo: string;
  issueDay: string;
  amount: string | null;
  currency: string | null;
  status: string | null;
  cancelReportStatus: string | null;
}

export interface LucaDocumentsResponse {
  from: string;
  to: string;
  hasMore: boolean;
  limit: number;
  items: LucaDocumentRow[];
}

export interface LucaDocRef {
  id: string;
  documentNo: string | null;
  issueDay: string | null;
}

export interface LucaShipmentRef {
  id: string;
  waybillNo: string | null;
  day: string | null;
  qtyFull: number | null;
  linkId: string | null;
}

export interface LucaReconciliation {
  from: string;
  to: string;
  despatch: {
    counts: { matched: number; lucaOnly: number; oursOnly: number; conflicts: number };
    matched: { document: LucaDocRef; shipments: LucaShipmentRef[]; via: 'AUTO' | 'LINK' }[];
    lucaOnly: { document: LucaDocRef; candidates: LucaShipmentRef[] }[];
    oursOnly: LucaShipmentRef[];
    conflicts: {
      reason: 'DUPLICATE_NUMBER' | 'LINK_MISMATCH';
      documents: LucaDocRef[];
      shipments: LucaShipmentRef[];
    }[];
  };
  archive: {
    day: string;
    luca: { count: number; cancelledCount: number; total: string };
    ours: { count: number; total: string; deposit: string; soldJugs: number };
  }[];
}

export type LucaSyncResult =
  | {
      status: 'ok';
      itemCount: number;
      createdCount: number;
      updatedCount: number;
      truncated: boolean;
    }
  | { status: 'failed'; code: string; detail?: string }
  | { status: 'skipped'; reason: string };

export interface SaveLucaConnectionInput {
  identificationNumber: string;
  password: string;
  consent: true;
  companyId?: number;
}

/** Düz işlev (hook değil): kimlik react-query önbelleğine girmez. */
export function saveLucaConnection(input: SaveLucaConnectionInput): Promise<LucaConnection> {
  return api.post<LucaConnection>('/api/luca/connection', input);
}

export function useLucaConnection() {
  return useQuery({
    queryKey: ['luca', 'connection'],
    queryFn: () => api.get<LucaConnection>('/api/luca/connection'),
  });
}

export function useLucaDocuments(type: LucaDocumentKind | null, from: string, to: string) {
  return useQuery({
    queryKey: ['luca', 'documents', type, from, to],
    queryFn: () =>
      api.get<LucaDocumentsResponse>(
        `/api/luca/documents?from=${from}&to=${to}${type ? `&type=${type}` : ''}`,
      ),
  });
}

export function useLucaReconciliation(from: string, to: string) {
  return useQuery({
    queryKey: ['luca', 'reconciliation', from, to],
    queryFn: () => api.get<LucaReconciliation>(`/api/luca/reconciliation?from=${from}&to=${to}`),
  });
}

export function useLucaMutations() {
  const qc = useQueryClient();
  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['luca'] });
  };
  return {
    activate: useMutation({
      mutationFn: () => api.post<LucaConnection>('/api/luca/connection/activate', {}),
      onSuccess: refresh,
    }),
    disconnect: useMutation({
      mutationFn: () => api.delete<LucaConnection>('/api/luca/connection'),
      onSuccess: refresh,
    }),
    sync: useMutation({
      mutationFn: () => api.post<LucaSyncResult>('/api/luca/sync', {}),
      onSuccess: refresh,
    }),
    createLink: useMutation({
      mutationFn: (v: { lucaDocumentId: string; shipmentId: string }) =>
        api.post<{ ok: true }>('/api/luca/links', v),
      onSuccess: refresh,
    }),
    revokeLink: useMutation({
      mutationFn: (id: string) => api.post<{ ok: true }>(`/api/luca/links/${id}/revoke`, {}),
      onSuccess: refresh,
    }),
  };
}
